/**
 * date-util — the single chokepoint for date formatting / parsing / comparison,
 * backed by date-fns. Replaces the app's former direct `moment` usage (T017).
 *
 * Design goals:
 *  - Preserve the app's existing *moment-style* format-token vocabulary
 *    (e.g. 'MM/DD/YYYY', 'L', 'YYYY-MM-DDTHH:mm:ss') so call sites don't have to
 *    learn date-fns tokens; the translation happens here.
 *  - Reproduce moment's exact output for every format/op the codebase uses.
 *    See date-util.spec.ts, whose expectations are the frozen moment baseline.
 *  - Accept native `Date` (what the NSwag client now emits), ISO strings, and
 *    epoch numbers — the same inputs `moment(value)` tolerated.
 */
import {
  addDays, addMonths, addYears,
  endOfDay, endOfMonth, endOfYear,
  format as dfFormat,
  formatISO,
  isValid as dfIsValid,
  parse as dfParse,
  parseISO,
  startOfDay, startOfMonth, startOfYear,
  subDays, subMonths, subYears,
} from 'date-fns';
import { SHORT_DATE_FORMAT } from '../constants';

export type DateInput = Date | string | number | null | undefined;
export type DateUnit = 'day' | 'days' | 'month' | 'months' | 'year' | 'years';

/** Coerce any accepted input to a `Date` (may be an Invalid Date). */
function coerce(value: DateInput): Date {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  if (typeof value === 'string') {
    const iso = parseISO(value);
    return dfIsValid(iso) ? iso : new Date(value);
  }
  return new Date(NaN);
}

/** 'day'|'days' -> 'day', etc. */
function unitKind(unit: DateUnit): 'day' | 'month' | 'year' {
  if (unit.indexOf('year') === 0) {
    return 'year';
  }
  if (unit.indexOf('month') === 0) {
    return 'month';
  }
  return 'day';
}

// en-US localized format tokens (moment.localeData('en-us')) -> their literal patterns.
const LOCALIZED_FORMATS: { [k: string]: string } = {
  LTS: 'h:mm:ss A', LT: 'h:mm A',
  LLLL: 'dddd, MMMM D, YYYY h:mm A', LLL: 'MMMM D, YYYY h:mm A', LL: 'MMMM D, YYYY', L: 'MM/DD/YYYY',
  llll: 'ddd, MMM D, YYYY h:mm A', lll: 'MMM D, YYYY h:mm A', ll: 'MMM D, YYYY', l: 'M/D/YYYY',
};
// moment field token -> date-fns field token. Note date-fns meridiem: `aa`=AM/PM (upper),
// `aaa`=am/pm (lower) — the reverse of moment's `A`/`a`.
const MOMENT_TOKENS: { [k: string]: string } = {
  YYYY: 'yyyy', YY: 'yy',
  MMMM: 'MMMM', MMM: 'MMM', MM: 'MM', Mo: 'Mo', M: 'M',
  DD: 'dd', Do: 'do', D: 'd',
  dddd: 'EEEE', ddd: 'EEE',
  HH: 'HH', H: 'H', hh: 'hh', h: 'h',
  mm: 'mm', m: 'm', SSS: 'SSS', ss: 'ss', s: 's',
  A: 'aa', a: 'aaa',
  ZZ: 'xx', Z: 'xxx',
};
const LOCALIZED_RE = /LTS|LT|LLLL|LLL|LL|llll|lll|ll|L|l/g;
const TOKEN_RE = /^(YYYY|YY|MMMM|MMM|MM|Mo|M|DD|Do|D|dddd|ddd|HH|H|hh|h|mm|m|SSS|ss|s|A|a|ZZ|Z)/;

/**
 * Translate a moment format string to the equivalent date-fns format string,
 * verified byte-for-byte against moment@2.29.4 (see date-util.spec.ts). Localized
 * tokens (L/LL/LLL/LT/...) are expanded to their en-US patterns, moment field
 * tokens are mapped to date-fns, and any literal latin letter (e.g. the `T` in an
 * ISO pattern) is single-quote escaped as date-fns requires.
 */
export function momentFormatToDateFns(momentFormat: string): string {
  const expanded = momentFormat.replace(LOCALIZED_RE, (m) => LOCALIZED_FORMATS[m] || m);
  let out = '';
  let i = 0;
  while (i < expanded.length) {
    const match = expanded.slice(i).match(TOKEN_RE);
    if (match) {
      out += MOMENT_TOKENS[match[0]];
      i += match[0].length;
    } else {
      const ch = expanded[i];
      out += /[A-Za-z]/.test(ch) ? `'${ch}'` : ch;
      i += 1;
    }
  }
  return out;
}

/**
 * Format a date using the app's moment-style tokens. Empty/undefined `format`
 * yields moment's default ISO-8601-with-offset (e.g. 2020-04-06T09:05:00-05:00).
 */
export function formatDate(value: DateInput, format: string = SHORT_DATE_FORMAT): string {
  if (!value) {
    return '';
  }
  const date = coerce(value);
  if (!dfIsValid(date)) {
    return '';
  }
  if (!format) {
    return formatISO(date);
  }
  return dfFormat(date, momentFormatToDateFns(format));
}

/** moment(value).format() with no format — ISO-8601 with local offset. */
export function formatIso(value: DateInput): string {
  if (!value) {
    return '';
  }
  const date = coerce(value);
  return dfIsValid(date) ? formatISO(date) : '';
}

/**
 * Parse a string with an explicit moment format (the app only uses 'YYYY').
 * Missing units default to Jan 1 00:00 — matching moment's start-of-unit fill.
 */
export function parseWithFormat(value: string, momentFormat: string): Date {
  return dfParse(value, momentFormatToDateFns(momentFormat), new Date(2000, 0, 1));
}

/** Coerce to a `Date`, or `null` for falsy/invalid input. */
export function toDate(value: DateInput): Date | null {
  if (!value) {
    return null;
  }
  const date = coerce(value);
  return dfIsValid(date) ? date : null;
}

/** moment(value).isValid() */
export function isValidDate(value: DateInput): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  return dfIsValid(coerce(value));
}

/** Current date/time (moment()). */
export function nowDate(): Date {
  return new Date();
}

/** moment(value).startOf(unit) */
export function startOf(value: DateInput, unit: DateUnit): Date {
  const date = coerce(value);
  switch (unitKind(unit)) {
    case 'year': return startOfYear(date);
    case 'month': return startOfMonth(date);
    default: return startOfDay(date);
  }
}

/** moment(value).endOf(unit) */
export function endOf(value: DateInput, unit: DateUnit): Date {
  const date = coerce(value);
  switch (unitKind(unit)) {
    case 'year': return endOfYear(date);
    case 'month': return endOfMonth(date);
    default: return endOfDay(date);
  }
}

/** moment(value).add(amount, unit) */
export function addTime(value: DateInput, amount: number, unit: DateUnit): Date {
  const date = coerce(value);
  switch (unitKind(unit)) {
    case 'year': return addYears(date, amount);
    case 'month': return addMonths(date, amount);
    default: return addDays(date, amount);
  }
}

/** moment(value).subtract(amount, unit) */
export function subtractTime(value: DateInput, amount: number, unit: DateUnit): Date {
  const date = coerce(value);
  switch (unitKind(unit)) {
    case 'year': return subYears(date, amount);
    case 'month': return subMonths(date, amount);
    default: return subDays(date, amount);
  }
}

/** Truncate to the start of a granularity unit (for granular comparisons). */
function truncate(value: DateInput, unit?: DateUnit): number {
  return (unit ? startOf(value, unit) : coerce(value)).getTime();
}

/** moment(a).isSame(b, [unit]) */
export function isSame(a: DateInput, b: DateInput, unit?: DateUnit): boolean {
  return truncate(a, unit) === truncate(b, unit);
}

/** moment(a).isBefore(b, [unit]) */
export function isBefore(a: DateInput, b: DateInput, unit?: DateUnit): boolean {
  return truncate(a, unit) < truncate(b, unit);
}

/** moment(a).isAfter(b, [unit]) */
export function isAfter(a: DateInput, b: DateInput, unit?: DateUnit): boolean {
  return truncate(a, unit) > truncate(b, unit);
}

/** moment(a).isSameOrBefore(b, [unit]) */
export function isSameOrBefore(a: DateInput, b: DateInput, unit?: DateUnit): boolean {
  return truncate(a, unit) <= truncate(b, unit);
}

/** moment(a).isSameOrAfter(b, [unit]) */
export function isSameOrAfter(a: DateInput, b: DateInput, unit?: DateUnit): boolean {
  return truncate(a, unit) >= truncate(b, unit);
}

/**
 * moment(value).isBetween(start, end, [unit], [inclusivity]).
 * `inclusivity` is a moment-style bracket pair; '[' / ']' = inclusive edge,
 * '(' / ')' = exclusive edge. moment's default is exclusive on both ends.
 */
export function isBetween(
  value: DateInput,
  start: DateInput,
  end: DateInput,
  unit?: DateUnit,
  inclusivity: string = '()',
): boolean {
  const v = truncate(value, unit);
  const s = truncate(start, unit);
  const e = truncate(end, unit);
  const lower = inclusivity[0] === '[' ? v >= s : v > s;
  const upper = inclusivity[1] === ']' ? v <= e : v < e;
  return lower && upper;
}
